import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Home, ListTodo, Timer, CalendarDays, BarChart3, Settings } from 'lucide-react';
import { onAuth, useAppStore, useTimerStore, onTasks, onProjects, useAudio, useSettingsSync } from '@flowfocus/core';
import type { User } from 'firebase/auth';
import { TimerProvider, useTimer } from './hooks/TimerContext';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useTaskReminders } from './hooks/useTaskReminders';
import { useBlockerBridge } from './lib/blockerBridge';
import { Logo } from './components/Logo';
import { FloatingPill } from './components/FloatingPill';
import { FocusOverlay } from './components/FocusOverlay';
import { TasksPage } from './pages/TasksPage';
import { TimerPage } from './pages/TimerPage';
import { CalendarPage } from './pages/CalendarPage';
import { StatsPage } from './pages/StatsPage';
import { SettingsPage } from './pages/SettingsPage';
import { AuthPage } from './pages/AuthPage';
import { HubPage } from './pages/HubPage';

const NAV = [
  { path: '/hoy',      label: 'Hoy',           Icon: Home },
  { path: '/',         label: 'Tareas',        Icon: ListTodo },
  { path: '/timer',    label: 'Temporizador',  Icon: Timer },
  { path: '/calendar', label: 'Calendario',    Icon: CalendarDays },
  { path: '/stats',    label: 'Estadísticas',  Icon: BarChart3 },
  { path: '/settings', label: 'Ajustes',       Icon: Settings },
];

export default function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const { setTasks, setProjects } = useAppStore();
  const settings = useTimerStore((s) => s.settings);

  useAudio(settings.whiteNoise);
  useSettingsSync(user?.uid);

  // Theme
  useEffect(() => {
    const apply = (dark: boolean) => document.documentElement.classList.toggle('dark', dark);
    if (settings.darkMode === 'dark') { apply(true); return; }
    if (settings.darkMode === 'light') { apply(false); return; }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    apply(mq.matches);
    const h = (e: MediaQueryListEvent) => apply(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [settings.darkMode]);

  useEffect(() => onAuth((u) => { setUser(u); setAuthLoading(false); }), []);

  useEffect(() => {
    if (!user) return;
    const a = onTasks(user.uid, setTasks);
    const b = onProjects(user.uid, setProjects);
    return () => { a(); b(); };
  }, [user?.uid]);

  if (authLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-app)' }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', border: '2.5px solid var(--primary)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  if (!user) return <AuthPage />;

  return (
    <TimerProvider uid={user.uid}>
      <Shell user={user} />
    </TimerProvider>
  );
}

function Shell({ user }: { user: User }) {
  const navigate = useNavigate();
  const location = useLocation();
  const timer = useTimer();

  useKeyboardShortcuts({
    onToggle: () => (timer.status === 'running' ? timer.pause() : timer.status === 'paused' ? timer.resume() : timer.start(timer.taskId)),
    onSkip: timer.skip,
    onReset: timer.reset,
    onFullscreen: timer.openFullscreen,
    onNew: () => navigate('/'),
  });

  // Fire best-effort notifications for tasks with a reminder time.
  useTaskReminders();

  // Ask the browser extension to block distracting sites while focusing here.
  useBlockerBridge(timer.status === 'running' && timer.sessionType === 'work', timer.snap?.endsAt ?? undefined);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: 'var(--bg-app)' }}>
      {/* Desktop sidebar */}
      <aside className="sidebar-desktop" style={{ width: 230, flexShrink: 0, background: 'var(--bg-sidebar)', borderRight: '0.5px solid var(--border)', display: 'flex', flexDirection: 'column', padding: '18px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 6px 18px' }}>
          <Logo size={30} />
          <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.4px' }}>FlowFocus</span>
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
          {NAV.map(({ path, label, Icon }) => (
            <div key={path} className={`nav-item ${location.pathname === path ? 'active' : ''}`} onClick={() => navigate(path)}>
              <Icon size={18} /> <span>{label}</span>
            </div>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 6px 0', borderTop: '0.5px solid var(--border)' }}>
          <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--primary)', color: 'var(--on-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
            {(user.displayName ?? user.email ?? 'U').slice(0, 1).toUpperCase()}
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-mut)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{user.displayName ?? user.email}</span>
        </div>
      </aside>

      <main className="main-with-sidebar" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div key={location.pathname} className="page-in" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Routes>
          <Route path="/hoy"      element={<HubPage uid={user.uid} />} />
          <Route path="/"         element={<TasksPage uid={user.uid} />} />
          <Route path="/timer"    element={<TimerPage uid={user.uid} />} />
          <Route path="/calendar" element={<CalendarPage uid={user.uid} />} />
          <Route path="/stats"    element={<StatsPage uid={user.uid} />} />
          <Route path="/settings" element={<SettingsPage uid={user.uid} user={user} />} />
          <Route path="*"         element={<Navigate to="/" replace />} />
        </Routes>
        </div>
      </main>

      {/* Mobile tabs */}
      <nav className="mobile-tabbar">
        {NAV.map(({ path, label, Icon }) => (
          <button key={path} className={location.pathname === path ? 'on' : ''} onClick={() => navigate(path)}>
            <Icon size={21} strokeWidth={location.pathname === path ? 2.4 : 2} />
            {label}
          </button>
        ))}
      </nav>

      {timer.status !== 'idle' && !timer.fullscreen && location.pathname !== '/timer' && <FloatingPill />}
      {timer.fullscreen && <FocusOverlay uid={user.uid} />}
    </div>
  );
}
